"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import ProtectedRoute from "@/components/ProtectedRoute";
import ArtisanSidebar from "@/components/ArtisanSidebar";
import { useApp } from "@/contexts/AppContext";
import {
  FaExclamationTriangle,
  FaUtensils,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaUserTie,
} from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import {
  Container,
  Content,
  MainContent,
  PageHeader,
  PageTitle,
  PageSubtitle,
  StatsGrid,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  SectionGrid,
  Section,
  SectionHeader,
  SectionTitle,
  ViewAllButton,
  OrderItem,
  OrderHeader,
  OrderId,
  OrderStatus,
  OrderDetails,
  LoadingContainer,
  Spinner,
  EmptyState,
  ReminderCard,
  ReminderHeader,
  ReminderIcon,
  ReminderContent,
  ReminderTitle,
  ReminderText,
  ReminderButton,
  ProfitsCard,
  ProfitsHeader,
  ProfitsTitle,
  RefreshButton,
  ProfitsAmount,
  ProfitsSubtext,
  ProfitsInfo,
  ProfitsInfoItem,
  ProfitsInfoLabel,
  ProfitsInfoValue,
  ProfitsSpinner,
  ProfitsError,
  LastUpdated,
  ProfitsDisclaimer,
} from "./page.styles";

interface DashboardStats {
  totalMeals: number;
  activeMeals: number;
  pendingOrders: number;
  totalOrders: number;
  totalEarnings: number;
}

interface ProfitsData {
  pending: number;
  available: number;
  lastFetched: Date | null;
}

interface Order {
  id: string;
  userName: string;
  total: number;
  status: string;
  createdAt: any;
  items: any[];
}

export default function ArtisanDashboardPage() {
  const router = useRouter();
  const { user } = useApp();
  const [stats, setStats] = useState<DashboardStats>({
    totalMeals: 0,
    activeMeals: 0,
    pendingOrders: 0,
    totalOrders: 0,
    totalEarnings: 0,
  });
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [stripeConnected, setStripeConnected] = useState(true);
  const [profits, setProfits] = useState<ProfitsData>({
    pending: 0,
    available: 0,
    lastFetched: null,
  });
  const [profitsLoading, setProfitsLoading] = useState(false);
  const [profitsError, setProfitsError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  useEffect(() => {
    if (user && stripeConnected) {
      // Load profits data if not recently fetched
      const shouldFetchProfits =
        !profits.lastFetched ||
        Date.now() - profits.lastFetched.getTime() > 5 * 60 * 1000; // 5 minutes cache

      if (shouldFetchProfits) {
        fetchProfitsBalance();
      }
    }
  }, [user, stripeConnected]);

  const fetchProfitsBalance = async () => {
    if (!user) return;

    // Validate user.uid before making the call
    if (!user.uid || user.uid.trim() === "") {
      console.error("fetchProfitsBalance: Invalid user.uid:", user.uid);
      setProfitsError("Invalid user ID");
      return;
    }

    setProfitsLoading(true);
    setProfitsError(null);

    try {
      const idToken = await user.getIdToken();
      const getCustomerBalanceV2 = httpsCallable(
        functions,
        "getCustomerBalanceV2"
      );

      const response = await getCustomerBalanceV2({
        sellerid: user.uid,
        idToken: idToken,
      });

      const data = response.data as any;

      // Check if Stripe account needs to be set up
      if (data.needsSetup || data.error === "Stripe account not connected") {
        setProfits({
          pending: 0,
          available: 0,
          lastFetched: new Date(),
        });
        setStats((prev) => ({ ...prev, totalEarnings: 0 }));
        setStripeConnected(false);
        return;
      }

      // Handle Stripe balance response format
      if (data.pending || data.available) {
        setProfits({
          pending: data.pending?.[0]?.amount || 0,
          available: data.available?.[0]?.amount || 0,
          lastFetched: new Date(),
        });
      } else {
        throw new Error(data.error || "Failed to fetch balance");
      }
    } catch (error: any) {
      console.error("Error fetching profits:", error);

      // Check if it's a functions error with details
      if (
        error.details?.needsSetup ||
        error.details?.error === "Stripe account not connected"
      ) {
        setProfits({
          pending: 0,
          available: 0,
          lastFetched: new Date(),
        });
        setStripeConnected(false);
        return;
      }

      // If it's a "needs setup" error, show zero balance instead of error
      if (
        error.message?.includes("Stripe account not connected") ||
        error.message?.includes("needsSetup") ||
        error.code === "functions/invalid-argument" ||
        error.code === "functions/failed-precondition"
      ) {
        setProfits({
          pending: 0,
          available: 0,
          lastFetched: new Date(),
        });
        setStripeConnected(false);
      } else {
        setProfitsError(
          error.message || "Unable to load profits. Please try again."
        );
      }
    } finally {
      setProfitsLoading(false);
    }
  };

  const handleRefreshProfits = () => {
    fetchProfitsBalance();
  };

  const formatAmount = (cents: number): string => {
    return (cents / 100).toFixed(2);
  };

  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return "Never";
    const now = Date.now();
    const diff = now - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };

  const loadDashboardData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Load user account data to check Stripe connection status
      const userDocRef = doc(db, "accounts", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setStripeConnected(userData.stripeConnected || false);
      }

      // Load meals stats
      const mealsRef = collection(db, "meals");
      const mealsQuery = query(mealsRef, where("uid", "==", user.uid));
      const mealsSnapshot = await getDocs(mealsQuery);

      const totalMeals = mealsSnapshot.size;
      const activeMeals = mealsSnapshot.docs.filter(
        (doc) => doc.data().available === 0
      ).length;

      // Load orders for artisan's meals
      const mealIds = mealsSnapshot.docs.map((doc) => doc.id);

      if (mealIds.length > 0) {
        const ordersRef = collection(db, "orders");
        const ordersSnapshot = await getDocs(ordersRef);

        // Filter orders that contain artisan's meals
        const artisanOrders = ordersSnapshot.docs.filter((doc) => {
          const orderData = doc.data();
          return orderData.items?.some(
            (item: any) => item.artisanId === user.uid
          );
        });

        const totalOrders = artisanOrders.length;
        const pendingOrders = artisanOrders.filter(
          (doc) => doc.data().status === "pending"
        ).length;

        // Calculate total earnings
        const totalEarnings = artisanOrders.reduce((sum, doc) => {
          const orderData = doc.data();
          const artisanItemsTotal =
            orderData.items
              ?.filter((item: any) => item.artisanId === user.uid)
              .reduce(
                (itemSum: number, item: any) => itemSum + item.subtotal,
                0
              ) || 0;
          return sum + artisanItemsTotal;
        }, 0);

        // Get recent orders
        const recentOrdersData = artisanOrders
          .sort((a, b) => {
            const aTime = a.data().createdAt?.toMillis() || 0;
            const bTime = b.data().createdAt?.toMillis() || 0;
            return bTime - aTime;
          })
          .slice(0, 5)
          .map((doc) => ({
            id: doc.id,
            userName: doc.data().userName,
            total:
              doc
                .data()
                .items?.filter((item: any) => item.artisanId === user.uid)
                .reduce((sum: number, item: any) => sum + item.subtotal, 0) ||
              0,
            status: doc.data().status,
            createdAt: doc.data().createdAt,
            items:
              doc
                .data()
                .items?.filter((item: any) => item.artisanId === user.uid) ||
              [],
          }));

        setStats({
          totalMeals,
          activeMeals,
          pendingOrders,
          totalOrders,
          totalEarnings,
        });
        setRecentOrders(recentOrdersData);
      } else {
        setStats({
          totalMeals,
          activeMeals,
          pendingOrders: 0,
          totalOrders: 0,
          totalEarnings: 0,
        });
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "Unknown";
    try {
      const date = timestamp.toDate();
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      }).format(date);
    } catch {
      return "Unknown";
    }
  };

  if (loading) {
    return (
      <ProtectedRoute>
        <Container>
          <AppHeader
            showNav={true}
            showGreeting={false}
            showOrders={false}
            currentPage="artisan-dashboard"
            logoLink="/"
          />
          <Content>
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          </Content>
        </Container>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <Container>
        <AppHeader
          showNav={true}
          showGreeting={false}
          showOrders={false}
          currentPage="artisan-dashboard"
          logoLink="/"
        />

        <Content>
          <ArtisanSidebar stripeConnected={stripeConnected} />

          <MainContent>
            {!stripeConnected && (
              <ReminderCard>
                <ReminderHeader>
                  <ReminderIcon>
                    <FaExclamationTriangle size={24} />
                  </ReminderIcon>
                  <ReminderContent>
                    <ReminderTitle>Link Your Bank Account</ReminderTitle>
                    <ReminderText>
                      You need to link your bank account information in order to
                      create meals. Drizl needs to transfer money into your
                      account when you sell your meals.
                    </ReminderText>
                    <ReminderButton
                      onClick={() =>
                        router.push("/artisan-dashboard/link-bank-account")
                      }
                    >
                      Link Bank Account →
                    </ReminderButton>
                  </ReminderContent>
                </ReminderHeader>
              </ReminderCard>
            )}

            <PageHeader>
              <PageTitle>
                <FaUserTie style={{ marginRight: "8px" }} /> Artisan Dashboard
              </PageTitle>
              <PageSubtitle>
                Welcome back! Here&apos;s what&apos;s happening with your
                business.
              </PageSubtitle>
            </PageHeader>

            <StatsGrid>
              <StatCard>
                <StatIcon $color="#C8553D">
                  <FaUtensils size={24} />
                </StatIcon>
                <div>
                  <StatValue>{stats.totalMeals}</StatValue>
                  <StatLabel>Total Meals</StatLabel>
                </div>
              </StatCard>

              <StatCard>
                <StatIcon $color="#558B8D">
                  <FaCheckCircle size={24} />
                </StatIcon>
                <div>
                  <StatValue>{stats.activeMeals}</StatValue>
                  <StatLabel>Active Meals</StatLabel>
                </div>
              </StatCard>

              <StatCard>
                <StatIcon $color="#F2853B">
                  <FaClock size={24} />
                </StatIcon>
                <div>
                  <StatValue>{stats.pendingOrders}</StatValue>
                  <StatLabel>Pending Orders</StatLabel>
                </div>
              </StatCard>
            </StatsGrid>

            {stripeConnected && (
              <ProfitsCard>
                <ProfitsHeader>
                  <ProfitsTitle>
                    <FaCreditCard style={{ marginRight: "8px" }} /> Your Profits
                  </ProfitsTitle>
                  <RefreshButton
                    onClick={handleRefreshProfits}
                    disabled={profitsLoading}
                  >
                    {profitsLoading ? (
                      <ProfitsSpinner />
                    ) : (
                      <>
                        <MdRefresh style={{ marginRight: "6px" }} /> Refresh
                      </>
                    )}
                  </RefreshButton>
                </ProfitsHeader>

                {profitsError ? (
                  <ProfitsError>{profitsError}</ProfitsError>
                ) : (
                  <>
                    <ProfitsAmount>
                      ${formatAmount(profits.pending + profits.available)}
                    </ProfitsAmount>
                    <ProfitsSubtext>
                      Total balance across pending and available funds. This
                      includes state sales tax collected, which is transferred
                      to you along with your earnings. Note: Drizl collects only
                      state-level sales tax. You may be liable for local taxes
                      depending on your jurisdiction.
                    </ProfitsSubtext>

                    <ProfitsInfo>
                      <ProfitsInfoItem>
                        <ProfitsInfoLabel>Pending Balance</ProfitsInfoLabel>
                        <ProfitsInfoValue>
                          ${formatAmount(profits.pending)}
                        </ProfitsInfoValue>
                      </ProfitsInfoItem>
                      <ProfitsInfoItem>
                        <ProfitsInfoLabel>Available Balance</ProfitsInfoLabel>
                        <ProfitsInfoValue>
                          ${formatAmount(profits.available)}
                        </ProfitsInfoValue>
                      </ProfitsInfoItem>
                      <ProfitsInfoItem>
                        <ProfitsInfoLabel>Payout Schedule</ProfitsInfoLabel>
                        <ProfitsInfoValue>Rolling basis</ProfitsInfoValue>
                      </ProfitsInfoItem>
                    </ProfitsInfo>

                    <LastUpdated>
                      Last updated: {formatLastUpdated(profits.lastFetched)}
                    </LastUpdated>

                    <ProfitsDisclaimer>
                      <strong>Platform Fee:</strong> Drizl takes a 15% fee from
                      each sale to cover credit card processing and platform
                      fees. Your profits shown above reflect the amounts after
                      this fee has been deducted.
                    </ProfitsDisclaimer>
                  </>
                )}
              </ProfitsCard>
            )}

            <SectionGrid>
              <Section>
                <SectionHeader>
                  <SectionTitle>Recent Orders</SectionTitle>
                  <ViewAllButton
                    onClick={() => router.push("/artisan-dashboard/orders")}
                  >
                    View All
                  </ViewAllButton>
                </SectionHeader>

                {recentOrders.length > 0 ? (
                  recentOrders.map((order) => (
                    <OrderItem
                      key={order.id}
                      onClick={() => router.push(`/orders/${order.id}`)}
                    >
                      <OrderHeader>
                        <OrderId>Order #{order.id.slice(0, 8)}</OrderId>
                        <OrderStatus $status={order.status}>
                          {order.status}
                        </OrderStatus>
                      </OrderHeader>
                      <OrderDetails>
                        {order.userName} • ${order.total.toFixed(2)} •{" "}
                        {order.items.length} items
                        <br />
                        {formatDate(order.createdAt)}
                      </OrderDetails>
                    </OrderItem>
                  ))
                ) : (
                  <EmptyState>No orders yet</EmptyState>
                )}
              </Section>
            </SectionGrid>
          </MainContent>
        </Content>
        <Footer />
      </Container>
    </ProtectedRoute>
  );
}
